import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { ConfirmationModal } from '@procurify/ui'
import produce from 'immer'
import QueryString from 'query-string'

import {
  ExporterAPI,
  type ExportSampleResponseMetadata,
} from '@webapp/api/ExporterAPI'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  type ExporterColumn,
  type ExportProgress,
  type User,
} from '@webapp/models'
import { type ExportType } from '@webapp/models/enums'
import { type IApiResponse } from '@webapp/services/api'
import { type Enum } from '@webapp/utils'
import { ExporterExportForm } from './components/ExporterExportForm'
import { type IExporterExportFormProps } from './components/ExporterExportForm/ExporterExportForm'
import { ExporterFormatForm } from './components/ExporterFormatForm'
import { type IExporterFormatFormProps } from './components/ExporterFormatForm/ExporterFormatForm'
import messages from './messages'
import { ExporterStyled } from './styles/ExporterStyled'
import { type ExportFormat } from './types'
import { ExporterProcessingStatus } from '../ExporterProcessingStatus'
import { type IExporterProcessingStatusProps } from '../ExporterProcessingStatus/ExporterProcessingStatus'

export interface IExporterProps {
  type: ExportType
  /**
   * The base endpoint that supports export functions via
   *  - ${baseEndpoint}/export-sample
   *  - ${baseEndpoint}/export-row-count
   *  - ${baseEndpoint}/export
   */
  baseEndpoint: string
  /**
   * The body/payload that will be passed into the final export submission
   * POST request
   */
  exportParams?: Record<string, any>
  /**
   * The query params that will be passed into the final export submission
   * POST request as a query string
   */
  exportQueryParams?: Record<string, any>
  /**
   * The params that will be passed into the final export submission
   * api request
   */
  exportSampleParams?: Record<string, any>
  /**
   * Additional toggles that appears at the bottom of the form
   */
  additionalToggles?: {
    defaultValue?: boolean
    key: string
    label: string
  }[]
  renderExportFormFilters?: ReactNode
  exportStatusUrl?: string
  userEmail?: User['email']
  emailExportMode?: boolean
  onStateChange?: (state: ExporterState) => void
  onSampleResponse?: (
    res: IApiResponse<string[][], ExportSampleResponseMetadata>
  ) => void
  onSubmitSuccess?: (data: ExportProgress) => void
  onSubmitError?: (err) => void
  onCancel?: () => void
}

export const EXPORTER_STATES = {
  FORM: 0,
  CREATE: 1,
  EDIT: 2,
  PROCESSING: 3,
} as const

export type ExporterState = Enum<typeof EXPORTER_STATES>

/**
 * The exporter has no error UI of its own, but every rejection still has to be
 * handled — an unhandled one is reported to Sentry as a stackless
 * "Non-Error promise rejection" from the legacy api client.
 */
const ignoreError = () => undefined

const DEFAULT_EXPORT_FORMAT: ExportFormat = {
  columns: null,
  id: -1,
  name: 'Default Format',
  date_format: null,
  decimal_point: null,
  is_default: false,
  type: null,
}

export const Exporter = ({
  type,
  baseEndpoint,
  exportParams = {},
  exportQueryParams,
  exportSampleParams,
  additionalToggles = [],
  exportStatusUrl,
  renderExportFormFilters = <></>,
  userEmail,
  emailExportMode = false,
  onSampleResponse = () => undefined,
  onStateChange = () => undefined,
  onSubmitSuccess = () => undefined,
  onSubmitError = () => undefined,
  onCancel = () => undefined,
}: IExporterProps) => {
  const intl = useProcurifyIntl()
  const [formState, setFormState] = useState<ExporterState>(
    EXPORTER_STATES.FORM
  )
  const [exportFormatOptions, setExportFormatOptions] = useState<
    ExportFormat[]
  >([])
  const [selectedFormatOption, setSelectedFormatOption] =
    useState<ExportFormat>(null)
  const [defaultFormatOptionId, setDefaultOptionId] = useState<number>()
  const [sampleData, setSampleData] = useState([])
  const [isFetchingSampleData, setIsFetchhingSampleData] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [defaultExportColumns, setDefaultExportColumns] =
    useState<ExporterColumn[]>(null)
  const [activeExportColumns, setActiveExportColumns] =
    useState<ExporterColumn[]>(null)
  const [initialFormatData, setInitialFormatData] = useState<
    IExporterFormatFormProps['initialData']
  >({})
  const [csvSubscriptionOptions, setCsvSubscriptionOptions] = useState<{
    endpoint: string
    channel: string
    event: string
    filters: IExporterProcessingStatusProps['exportFilters']
    initialProgress: IExporterProcessingStatusProps['subscribe']['initialProgress']
  }>(null)

  const [formatOptionToDelete, setFormatOptionToDelete] =
    useState<ExportFormat>(null)

  useEffect(() => {
    ExporterAPI.getExportFormats({
      type,
      page_size: 9999,
    })
      .then((res) => res.data)
      .then((formatOptions) => {
        const defaultOption = formatOptions.find((option) => option.is_default)

        setSelectedFormatOption(defaultOption || DEFAULT_EXPORT_FORMAT)

        setDefaultOptionId(
          defaultOption ? defaultOption.id : DEFAULT_EXPORT_FORMAT.id
        )

        setExportFormatOptions([DEFAULT_EXPORT_FORMAT, ...formatOptions])
      })
      .catch(ignoreError)
  }, [])

  useEffect(() => {
    if (!selectedFormatOption) return

    setIsFetchhingSampleData(true)

    ExporterAPI.getExportSample(baseEndpoint, {
      export_format: selectedFormatOption.id,
      ...exportSampleParams,
    })
      .then((res) => {
        onSampleResponse(res)

        return res.data
      })
      .then((sample) => {
        setSampleData(sample)
      })
      .catch(ignoreError)
      .finally(() => {
        setIsFetchhingSampleData(false)
      })
  }, [selectedFormatOption, QueryString.stringify(exportSampleParams)])

  const onSetDefaultFormat = () => {
    if (selectedFormatOption.id === defaultFormatOptionId) return

    setDefaultOptionId(selectedFormatOption.id)

    if (selectedFormatOption.id === -1) {
      ExporterAPI.deleteDefaultFormatOption(type).catch(ignoreError)
    } else {
      ExporterAPI.setDefaultFormatOption(selectedFormatOption.id).catch(
        ignoreError
      )
    }
  }

  const onCreateNewFormat = async () => {
    if (!defaultExportColumns) {
      const columns = await ExporterAPI.getExportColumns(baseEndpoint)
        .then((res) => res.data)
        .catch(() => null)

      if (!columns) return

      setDefaultExportColumns(
        columns.map((col) => ({
          ...col,
          active: true,
        }))
      )
    }

    setFormState(EXPORTER_STATES.CREATE)
    onStateChange(EXPORTER_STATES.CREATE)
  }

  const onEditFormat = () => {
    setInitialFormatData({
      name: selectedFormatOption.name,
      date_format: selectedFormatOption.date_format,
      decimal_point: selectedFormatOption.decimal_point,
    })
    setActiveExportColumns(selectedFormatOption.columns)
    setFormState(EXPORTER_STATES.EDIT)
    onStateChange(EXPORTER_STATES.EDIT)
  }

  const onDeleteFormat = () => {
    setFormatOptionToDelete(selectedFormatOption)
  }

  const onDeleteFormatConfirm = (done) => {
    ExporterAPI.deleteFormatOption(selectedFormatOption.id)
      .then(() => {
        setFormatOptionToDelete(null)

        setExportFormatOptions(
          produce(exportFormatOptions, (draft) => {
            draft.splice(exportFormatOptions.indexOf(selectedFormatOption), 1)
          })
        )

        setSelectedFormatOption(exportFormatOptions[0])
        setDefaultOptionId(exportFormatOptions[0].id)
      })
      .catch(() => {
        // A failed delete keeps the modal open; re-enable its buttons so the
        // user can retry or dismiss. On success the modal unmounts, so done()
        // is only needed here.
        done()
      })
  }

  const onDeleteFormatDismiss = () => {
    setFormatOptionToDelete(null)
  }

  const onFormatFormCancel = () => {
    setFormState(EXPORTER_STATES.FORM)
    onStateChange(EXPORTER_STATES.FORM)
  }

  const onCreateFormatSubmit: IExporterFormatFormProps['onSubmit'] = (
    data,
    action
  ) => {
    ExporterAPI.createExportFormat({
      columns: data.columns,
      name: data.name,
      date_format: data.date_format.value,
      decimal_point: data.decimal_point.value === 'true',
      type,
    })
      .then((res) => res.data)
      .then((newFormat) => {
        setExportFormatOptions(exportFormatOptions.concat([newFormat]))
        setSelectedFormatOption(newFormat)

        setFormState(EXPORTER_STATES.FORM)
        onStateChange(EXPORTER_STATES.FORM)
      })
      .catch(ignoreError)
      .finally(() => {
        action.setSubmitting(false)
      })
  }

  const onEditFormatSubmit: IExporterFormatFormProps['onSubmit'] = (
    data,
    action
  ) => {
    ExporterAPI.updateExportFormat(selectedFormatOption.id, {
      ...selectedFormatOption,
      columns: data.columns,
      name: data.name,
      date_format: data.date_format.value,
      decimal_point: data.decimal_point.value === 'true',
    })
      .then((res) => res.data)
      .then((updatedFormat) => {
        setSelectedFormatOption({
          ...selectedFormatOption,
          ...updatedFormat,
        })
        setExportFormatOptions(
          produce(exportFormatOptions, (draft) => {
            const activeOptionIndex = draft.findIndex(
              (opt) => opt.id === selectedFormatOption.id
            )

            if (activeOptionIndex >= 0) {
              draft[activeOptionIndex] = {
                ...draft[activeOptionIndex],
                ...updatedFormat,
              }
            }
          })
        )
        setFormState(EXPORTER_STATES.FORM)
        onStateChange(EXPORTER_STATES.FORM)
      })
      .catch(ignoreError)
      .finally(() => {
        action.setSubmitting(false)
      })
  }

  const onSubmitExport: IExporterExportFormProps['onSubmit'] = ({
    exportEmail,
    ...additionalToggleParams
  }) => {
    setIsSubmitting(true)

    ExporterAPI.export(
      baseEndpoint,
      {
        export_email: exportEmail,
        export_format: selectedFormatOption.id,
        type,
        ...exportParams,
        ...additionalToggleParams,
      },
      exportQueryParams ? { params: exportQueryParams } : {}
    )
      .then((res) => res.data)
      .then((exportRes) => {
        const formattedFilters = exportRes.filters.map((filter) => {
          if (filter.value instanceof Array) {
            return {
              label: filter.label,
              value: filter.value.join(', '),
            }
          } else {
            return {
              label: filter.label,
              value: filter.value,
            }
          }
        })

        setCsvSubscriptionOptions({
          endpoint: `${baseEndpoint}export/`,
          event: exportRes.event,
          channel: exportRes.channel,
          filters: formattedFilters || [],
          initialProgress: {
            processedRows: exportRes.index,
            totalRows: exportRes.max,
          },
        })

        setFormState(EXPORTER_STATES.PROCESSING)
        onStateChange(EXPORTER_STATES.PROCESSING)

        setIsSubmitting(false)
        onSubmitSuccess(exportRes)
      })
      .catch((err) => {
        setIsSubmitting(false)
        onSubmitError(err)
      })
  }

  return (
    <ExporterStyled>
      {formatOptionToDelete && (
        <ConfirmationModal
          open
          type='danger'
          message={intl.formatMessage(
            messages.deleteFormatConfirmationMessage,
            {
              formatOptionName: formatOptionToDelete?.name,
            }
          )}
          confirmButtonText={intl.formatMessage(messages.deleteFormatButton)}
          header={intl.formatMessage(messages.deleteFormatConfirmationHeader)}
          onConfirm={onDeleteFormatConfirm}
          handleDismiss={onDeleteFormatDismiss}
        />
      )}
      {formState === EXPORTER_STATES.FORM && (
        <ExporterExportForm
          selectedFormat={selectedFormatOption}
          formatOptions={exportFormatOptions}
          isDefaultFormat={selectedFormatOption?.id === defaultFormatOptionId}
          isFormatEditable={
            selectedFormatOption ? selectedFormatOption.id !== -1 : false
          }
          renderFilters={renderExportFormFilters}
          isFetchingSampleData={isFetchingSampleData}
          isSubmitting={isSubmitting}
          onFormatSelect={setSelectedFormatOption}
          onEditFormat={onEditFormat}
          onDeleteFormat={onDeleteFormat}
          onCreateNewFormat={onCreateNewFormat}
          onSetDefaultFormat={onSetDefaultFormat}
          sampleData={sampleData}
          additionalToggles={additionalToggles}
          userEmail={userEmail}
          emailExportMode={emailExportMode}
          onSubmit={onSubmitExport}
          onCancel={onCancel}
        />
      )}
      {formState === EXPORTER_STATES.CREATE && (
        <ExporterFormatForm
          columns={defaultExportColumns}
          onSubmit={onCreateFormatSubmit}
          onCancel={onFormatFormCancel}
        />
      )}
      {formState === EXPORTER_STATES.EDIT && (
        <ExporterFormatForm
          initialData={initialFormatData}
          columns={activeExportColumns}
          onSubmit={onEditFormatSubmit}
          onCancel={onFormatFormCancel}
        />
      )}
      {formState === EXPORTER_STATES.PROCESSING && (
        <ExporterProcessingStatus
          exportStatusUrl={exportStatusUrl}
          exportFilters={csvSubscriptionOptions.filters}
          subscribe={{
            channel: csvSubscriptionOptions.channel,
            endpoint: csvSubscriptionOptions.endpoint,
            event: csvSubscriptionOptions.event,
            initialProgress: csvSubscriptionOptions.initialProgress,
          }}
        />
      )}
    </ExporterStyled>
  )
}

Exporter.displayName = 'Exporter'
